import type { ReactNode } from 'react';
import clsx from 'clsx';

import type { TextSizeType } from '../utils';
import type { Props as CellProps } from './Cell';

type Formatter<K> = (value: K) => ReactNode | null;

export type ColumnDefinition<T> = CellProps & {
  title: string;
  /** Leaves the header cell blank; `title` still keys the column. */
  isTitleHidden?: boolean;
} & (
    | {
        key: keyof T;
      }
    | { format: Formatter<T> }
  );

// We want to be as flexible as possible, but every row at least needs an ID
export type BaseRowType = {
  id: unknown;
  testId?: string;
};

export type BaseTableProps<T extends BaseRowType> = {
  data: readonly T[];
  schema: readonly ColumnDefinition<T>[];
  emptyValue?: Record<ColumnDefinition<T>['title'], ReactNode>;
  selected?: Pick<T, 'id'>;
  hasNextPage?: false;
  'data-testid'?: string;
  textSize?: TextSizeType;
  dottedHeader?: boolean;
  borderedRows?: boolean;
  stickyHeader?: boolean;
  rowHasNoAffect?: (row: T) => boolean;
  backgroundColor?: string;
  /**
   * Explicit `grid-template-columns` for every row. Columns take these widths
   * instead of their `colspan` ratios, and the spare width splits evenly
   * between them.
   */
  columnTemplate?: string;
  rowGap?: string;
};

export const gridClasses = (
  textSize: TextSizeType = 'sm',
  columnTemplate?: string
) =>
  clsx(
    'grid',
    'grid-flow-col', // dynamic number of columns
    'auto-cols-fr', // column sizes are a fixed ratio, unless a template sets them
    columnTemplate && 'justify-between', // spare width splits evenly between columns
    textSize === 'sm' ? 'px-f2' : 'px-f0',
    'gap-x-[1ch]'
  );

export const gridStyle = (columnTemplate?: string) =>
  columnTemplate ? { gridTemplateColumns: columnTemplate } : undefined;
